/**
 * ==============================================
 * MONTHS SUBSCRIPTION PICKER - WEB COMPONENT
 * ==============================================
 *
 * Custom HTML element <subscription-picker> that manages Bob's subscription product selection flow.
 *
 * CORE RESPONSIBILITIES:
 * - Manage refill frequency selection (Monthly/Quarterly/One-time)
 * - Handle scent pack selection with quantity controls
 * - Calculate dynamic pricing based on customer type and selections
 * - Update UI elements (prices, images, feature lists)
 * - Validate selections before checkout
 * - Dispatch custom events for cart integration
 *
 * STATE MANAGEMENT:
 * - selectedRefillFrequency: Current frequency choice (monthly/quarterly/oneoff)
 * - selectedScents: Array of selected scent packs with quantities
 * - selectedAddons: Array of additional products (for one-time purchases)
 * - addonsTotalPrice: Total cost of add-ons in pence
 *
 * PRICING STRUCTURE:
 * - First-time customers: Get shower pack + refills at discounted rate
 * - Refill customers: Only pay for refills (no shower pack)
 * - Pricing differs by frequency and customer type
 *
 * INTEGRATION:
 * - Reads config data from JSON script tag (#months-product-subscription-picker-data)
 * - Integrates with Recharge for subscription management
 * - Communicates with mps--add-to-cart.js via custom events
 */
class MonthsSubscriptionPicker extends HTMLElement {
  constructor() {
    super();

    this.element = this;

    /**
     * Maps internal frequency values to display text
     * Used throughout the component for consistency
     */
    this.REFILL_FREQUENCY_MAP = {
      monthly: 'Every Month',
      quarterly: 'Every 3 Months', // Displayed as "Discovery Plan" in UI but maps to 'Every 3 Months' for pricing lookup
      trial: '14 Day Trial', // 14-day trial, transitions to quarterly via Recharge
      oneoff: 'One off Purchase',
      gift: 'Gift Purchase',
    };

    /**
     * Price per additional scent pack for one-time purchases (in pence)
     * £8.00 = 800 pence
     */
    this.SCENT_ADDON_PRICE_PENCE = 800;

    /**
     * Customer type determines which pricing tier to use
     * Options: 'FIRST TIME ORDER' or 'Recurring Order'
     * Currently hardcoded to FIRST TIME ORDER
     * TODO: Could be made dynamic based on customer history
     */
    this.CUSTOMER_TYPE = 'FIRST TIME ORDER';

    // Component state — default frequency set after configData is loaded below
    this.selectedRefillFrequency = 'quarterly';
    // Unique id of the selected plan card. Decoupled from frequency so two cards
    // may share a pricing frequency (e.g. a 6-week and a 3-month plan both
    // "quarterly"). Null for legacy (non plan-card) products, which key by frequency.
    this.selectedPlanId = null;
    this.refillProductOnly = this.element.dataset.refillProductOnly === 'true'; // Customer already has shower pack?
    this.addonsTotalPrice = 0; // Total cost of selected add-ons (pence)
    this.addonsBox = this.element.querySelector('.one-time-addons-box'); // Add-ons container element
    this.productUpsell = this.element.querySelector('[data-product-upsell]');
    this.productUpsellSelected = false;
    this.giftNotificationWidget = this.element.querySelector('gift-notification-widget'); // Gift notification widget element
    this.giftRecipientCount = 1; // Number of gift recipients (1-5)
    this.selectedScents = []; // Array of selected scent objects with quantities
    this.selectedScentSkusOrdered = []; // Ordered array of selected scent SKUs, e.g. ['x', 'x', 'y']
    this.selectedAddons = []; // Array of selected add-on products
    this.selectedColor = null; // Selected applicator color
    this.bundleTitleTemplate = null; // Cached authored bundle title (may contain the "[x]" pack-count placeholder)

    // Bind event handler for add-on changes (used with addEventListener)
    this.handleAddonChangeBinding = this.handleAddonChange.bind(this);
    this.handleProductUpsellToggleBinding = this.handleProductUpsellToggle.bind(this);
    this.handleProductUpsellSkipBinding = this.handleProductUpsellSkip.bind(this);

    /**
     * Load configuration data from JSON script tag
     * Contains product variants, images, and copy for different combinations
     */
    try {
      this.configData = JSON.parse(document.getElementById('months-product-subscription-picker-data').textContent);
      // Default frequency: read from the checked radio rendered by Liquid, fall back to first plan key
      const checkedRadio = this.querySelector('[name="refill_frequency"]:checked');
      this.selectedRefillFrequency = checkedRadio ? checkedRadio.value : Object.keys(this.configData.plan_cards_config || {})[0] || 'monthly';
      // Track the selected plan card's unique id (from the checked radio / its label).
      const checkedOption = checkedRadio ? checkedRadio.closest('.refill-option') : null;
      this.selectedPlanId = checkedRadio?.dataset.planId || checkedOption?.dataset.planId || null;
      // Initialize selected color from the rendered first non-excluded option.
      // Fall back to filtered JSON for states where the picker has no checked input yet.
      if (this.configData.color_option_values && this.configData.color_option_values.length > 0 && this.selectedRefillFrequency !== 'trial') {
        const checkedColorInput = this.querySelector('[name="applicator_color"]:checked');
        this.selectedColor = checkedColorInput?.value || this.configData.color_option_values[0];
      }
    } catch (error) {
      console.error('Error parsing products data:', error);
    }

    /**
     * One-time purchase settings
     * Can be overridden via window.oneTimeOffSettings if needed
     * This allows external scripts to customize one-time purchase behavior.
     *
     * PRICING LOGIC:
     * - refillProductOnly = true: Customer already has applicator, so they only pay for refills
     *   Price: £8 (800 pence) for 1 refill pack
     * - refillProductOnly = false: First-time customer gets full shower pack
     *   Price: £31.99 (3199 pence) for applicator + mount + 1 refill pack
     *
     * Note: One-time purchases have no discount (price_cents === price_cents_original)
     * so the strikethrough pricing is not shown in the UI for this option.
     */
    const one_time_refill_price = this.configData.one_time_refill_price || 800; // Default to £8.00 if not set in config
    let oneTimeOffSettings = {
      frequency: 'One off Purchase',
      price_cents: this.refillProductOnly ? one_time_refill_price * 2 : 4000, // £8 (refill only) or £40.00 (full pack)
      price_cents_original: this.refillProductOnly ? one_time_refill_price * 2 : 4000, // No discount for one-time
      whats_included_in_order: 'Shower pack',
      per_refill_pack_cost_cents: this.refillProductOnly ? one_time_refill_price : 900, // £8 (refill only) or £9.99 per additional scent
      barsQty: this.refillProductOnly ? 2 : 1, // Base quantity: 1 pack = 2 bars
      refill: {
        quantity: this.refillProductOnly ? 2 : 1,
        price_cents: this.refillProductOnly ? one_time_refill_price : 900,
      },
    };

    window.oneTimeOffSettings = window.oneTimeOffSettings || {};
    oneTimeOffSettings = { ...oneTimeOffSettings, ...window.oneTimeOffSettings };

    // Parse trial plan price from settings (e.g. "£11" or "£9.99") to pence, default 1100
    const trialPriceRaw = this.configData?.trial_plan_price || '£11';
    const trialPricePence = Math.round(parseFloat(trialPriceRaw.replace(/[^0-9.]/g, '')) * 100) || 1100;

    /**
     * ==============================================
     * PRICING MAP - CORE PRICING LOGIC
     * ==============================================
     * Defines all pricing tiers based on customer type and refill frequency.
     *
     * STRUCTURE:
     * {
     *   money_format: Shopify money format string
     *   'CUSTOMER_TYPE': [
     *     {
     *       frequency: Display name
     *       price_cents: First order price (discounted)
     *       price_cents_original: Original price (for strikethrough)
     *       per_refill_pack_cost_cents: Cost per individual refill pack
     *       refill: {
     *         price_cents: Price per refill delivery
     *         quantity: Number of packs per delivery
     *         frequency: How often
     *       }
     *     }
     *   ]
     * }
     *
     * PRICING BREAKDOWN:
     * - FIRST TIME ORDER prices include shower pack discount
     * - refillProductOnly flag adjusts prices (removes shower pack cost)
     * - Monthly: 1 pack per month
     * - Quarterly: 3 packs per 3 months (bulk discount)
     */
    /**
     * PRICING MAP - Master pricing configuration
     *
     * All prices are in PENCE (e.g., 700 = £7.00)
     * Formatted using Shopify.formatMoney() with money_format template
     */
    this.prices_map = {
      money_format: `£{{amount}}`, // Shopify money format template

      /**
       * FIRST TIME ORDER PRICING
       * These are customers who don't yet have the Bob applicator.
       * They receive the shower pack (applicator + mount) either FREE or at a discount.
       */
      'FIRST TIME ORDER': [
        /**
         * MONTHLY SUBSCRIPTION
         * Best value for first-time customers - FREE applicator
         * First order: £15 (discounted from £31.99) OR £12 for refill-only customers
         * Ongoing: £12/month for 2 refill packs (4 bars)
         */
        {
          frequency: 'Every Month',
          price_cents: this.refillProductOnly ? 1400 : 2200, // £12 refill-only (2 packs × £6) OR £15 first-time
          price_cents_original: this.refillProductOnly ? 1800 : 4000, // £19.98 refill-only OR £31.99 (for strikethrough)
          whats_included_in_order: 'Shower pack',
          per_refill_pack_cost_cents: 700, // £7 per pack for addon calculations
          refill: {
            price_cents: 700, // £6 per refill pack
            price_cents_original: 800, // £8 original per pack (for savings display)
            quantity: 2, // 2 packs per delivery (4 bars)
            frequency: 'Every Month',
          },
        },
        /**
         * QUARTERLY SUBSCRIPTION — "DISCOVERY PLAN"
         * Best value option — includes 4 refill packs (8 bars)
         * All available scents auto-selected (1 of each)
         * First order: £24 (discounted from £61.96) for first-time, £18 for refill-only
         * Ongoing: £24/3 months (4 packs × £6 each)
         */
        {
          frequency: 'Every 3 Months',
          price_cents: this.refillProductOnly ? 2800 : 2000,
          price_cents_original: this.refillProductOnly ? 3600 : 5800,
          whats_included_in_order: 'Shower pack + 4 x refill packs',
          per_refill_pack_cost_cents: 600, // £6 per pack (bulk discount)
          refill: {
            price_cents: 700, // £7 per pack in ongoing refills (4 × £7 = £28 / 3 months)
            price_cents_original: 800, // £8 original per pack
            quantity: 4, // 4 packs per delivery (8 bars total)
            frequency: 'Every 3 Months',
          },
        },
        /**
         * 14 DAY TRIAL PLAN
         * Trial period: £13.99 first order (Applicator + Shower Mount + 2 bars)
         * After 14 days: transitions to quarterly subscription (£24/3 months for 8 bars)
         * Customer selects 1 refill pack only
         * Uses product "14-days-trial-1" with its own Recharge selling plan
         */
        {
          frequency: '14 Day Trial',
          price_cents: trialPricePence, // from settings (trial_plan_price)
          price_cents_original: 3199, // £31.99
          whats_included_in_order: 'Shower pack + 1 refill pack',
          per_refill_pack_cost_cents: 600,
          refill: {
            price_cents: 2400,
            price_cents_original: 800,
            quantity: 1, // 1 scent pack for trial
            frequency: '14 Day Trial',
          },
        },
        /**
         * ONE-TIME PURCHASE
         * Imported from oneTimeOffSettings defined above
         * No subscription, no recurring charges
         */
        oneTimeOffSettings,
        /**
         * GIFT PURCHASE
         * One-time purchase intended as a gift
         * Fixed reference price £31.99 - includes shower pack + 1 refill
         * No discount shown (price_cents === price_cents_original)
         * Supports gift recipient notification feature
         */
        {
          frequency: 'Gift Purchase',
          price_cents: 2400, // £24 flat rate
          price_cents_original: 3199, // No discount (no strikethrough)
          whats_included_in_order: 'Shower pack + 1 refill pack',
          per_refill_pack_cost_cents: 800, // £8 if adding extra refills
          barsQty: 1, // 1 pack = 2 bars included
        },
      ],

      /**
       * RECURRING ORDER PRICING
       * For customers already in a subscription who are modifying their plan.
       * NOTE: This section appears unused in current implementation
       * (CUSTOMER_TYPE is always 'FIRST TIME ORDER')
       */
      'Recurring Order': [
        {
          frequency: 'Every Month',
          price_cents: 700, // £7 per refill pack
          whats_included_in_order: '1 x refill pack',
          per_refill_pack_cost_cents: 700,
        },
        {
          frequency: 'Every 3 Months',
          price_cents: 1800, // £18 for 3 packs (£6 each)
          whats_included_in_order: '3 x refill packs',
          per_refill_pack_cost_cents: 600,
        },
      ],
    };

    this.prices_map.money_format = this.configData?.money_format || this.prices_map.money_format;
    this.buildPricesMapFromPlanCards();

    this._initialized = false;

    // Initialize component
    this.initializeScentSelector();
    this.init();

    this._initialized = true;
  }

  /**
   * Initialize component after construction
   * Sets up event listeners and triggers initial state update
   */
  init() {
    this.bindEvents();
    this.setupGiftRecipientsListener();
    this.updateState();
    // Trigger change event on pre-selected frequency to update UI
    if (this.querySelector('[name="refill_frequency"]:checked')) {
      this.querySelector('[name="refill_frequency"]:checked').dispatchEvent(new Event('change', { bubbles: true }));
    }

    //this.preselectOrdinaryRefillOption();
    this.preselectEveningManRefillOptionForGift();
    this.updateTrialRenewalDate();
    this.autoAdvanceToStepTwoOnLoad();
    this.openDirectBuyboxWhenEnabled();

    // For trial plan, deselect the first color option so user must pick one
    /* if (this.selectedRefillFrequency === 'trial') {
      this.deselectAllColorOptions();
    } */
  }

  /**
   * Reset the media gallery slider to its first visible slide.
   *
   * The data-closed flip swaps which gallery slides are visible (step 1
   * metaobject images ↔ product images) via CSS only; the slider keeps its
   * old scrollLeft/currentPage, which point into the previous slide set, so
   * it shows a cut-off or blank slide. On scent-canvas PDPs the canvas
   * re-targets its own slide afterwards via the scentCardUpdated event.
   */
  resetGallerySlider() {
    const gallerySlider =
      this.element.closest('.shopify-section')?.querySelector('slider-element[id^="SliderGallery-"]') || document.querySelector('slider-element[id^="SliderGallery-"]');
    if (!gallerySlider) return;

    // The gallery reset below owns the complete reset transaction. Avoid
    // starting reinitialize()'s independent two-frame scroll reset first,
    // otherwise it can restore snapping while this reset is still in flight.
    gallerySlider.reinitialize?.({ resetScrollPosition: false });
    this.forceGalleryStart(gallerySlider);

    const mediaDots = gallerySlider.closest('media-gallery')?.querySelector('media-dots');
    if (mediaDots?.reset) mediaDots.reset();
    if (mediaDots?.resetIndexes) requestAnimationFrame(() => mediaDots.resetIndexes());

    // The scent canvas finishes its own two-frame injection after this method
    // returns. Reset again from that completion event so the injected slide
    // cannot restore the previous carousel offset.
    gallerySlider.addEventListener('scent-canvas:injected', () => {
      // The canvas injection changes which .product__media elements are
      // measurable. Rebuild the slider item registry only after that DOM
      // update has completed, then reset the freshly measured scroller.
      gallerySlider.reinitialize?.({ resetScrollPosition: false });
      this.forceGalleryStart(gallerySlider);
    }, { once: true });
  }

  forceGalleryStart(gallerySlider) {
    gallerySlider.resetScrollPosition?.({
      rebuildScrollContainer: gallerySlider.needsIOSScrollContainerReset,
    });
  }

  autoAdvanceToStepTwoOnLoad() {
    if (this.element.dataset.autoAdvanceToStepTwoOnLoad !== 'true') return;
    if (this.element.dataset.closed !== 'true') return;

    window.requestAnimationFrame(() => {
      if (this.element.dataset.closed === 'true') {
        this.handleAddToCart();
      }
    });
  }

  /**
   * Direct buybox (A/B variant, assets/pdp-buybox-direct.css)
   *
   * When <html> has the class test-pdp-buybox-direct, set by the A/B tool
   * before or after this runs, the picker opens at step 2 with no Continue
   * click. Without the class nothing changes.
   */
  openDirectBuyboxWhenEnabled() {
    const openIfEnabled = () => {
      if (!document.documentElement.classList.contains('test-pdp-buybox-direct')) return false;
      this.openWithoutContinue();
      return true;
    };

    if (openIfEnabled()) return;

    const observer = new MutationObserver(() => {
      if (openIfEnabled()) observer.disconnect();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Direct buybox: when the visitor picks an applicator colour, show the scent
   * canvas (chosen colour + selected scents) and scroll the gallery to it, as
   * the Continue click does in the current buybox.
   */
  showSelectionInGallery() {
    this.dispatchCustomEvent('scentCardUpdated', { scentSku: this.selectedScentSku, selectedScentSkusOrdered: this.selectedScentSkusOrdered });
  }

  /**
   * Same switch to step 2 as the first handleAddToCart() click, minus the
   * gallery reset and the scentCardUpdated event (so the landing image is the
   * same as the current buybox) and minus the "continue" analytics.
   */
  openWithoutContinue() {
    if (this.element.dataset.closed !== 'true') return;

    this.directBuyboxOpen = true;
    this.element.setAttribute('data-closed', 'false');
    this.element.dataset.currentStep = '2';
    const btnText = this.element.querySelector('[data-add-to-cart-btn] .btn-text');
    if (btnText) btnText.innerText = 'Checkout';

    const selectedColor = this.element.querySelector('.color-option--selected');
    if (selectedColor) this.handleColorSelection(null, selectedColor, true);
    this.updateAddToCartButton();
  }

  /**
   * Pre-select "The Ordinary Man" refill option automatically
   *
   * CURRENTLY DISABLED: Called from init() but commented out there.
   * When enabled, this would:
   * 1. Auto-click the "Ordinary Man" scent add button multiple times with delays
   * 2. Listen for frequency changes and auto-add Ordinary Man on each change
   *
   * PRODUCT ID: 15172234543490 = "The Ordinary Man" scent product
   *
   * PURPOSE:
   * This was likely used for testing or a promotion where Ordinary Man
   * was the default/featured scent selection. Clicking multiple times
   * fills the required quantity for different frequencies (monthly=1, quarterly=3).
   *
   * WHY DISABLED:
   * Pre-selecting scents may confuse customers who want to choose their own.
   * Could be re-enabled for promotional campaigns featuring a specific scent.
   */
  preselectOrdinaryRefillOption() {
    const ordinaryManAdd = document.querySelector(`[data-product-id="15172234543490"] [data-action="add"]`);
    if (ordinaryManAdd) {
      ordinaryManAdd.click();
      setTimeout(() => {
        ordinaryManAdd.click();
      }, 500);
      setTimeout(() => {
        ordinaryManAdd.click();
      }, 1500);
    }
    window.addEventListener('subscription-picker:refillFrequencyChanged', (event) => {
      const ordinaryManAdd = document.querySelector(`[data-product-id="15172234543490"] [data-action="add"]`);
      if (ordinaryManAdd) ordinaryManAdd.click();
    });
  }

  preselectEveningManRefillOptionForGift() {
    window.addEventListener('subscription-picker:refillFrequencyChanged', (event) => {
      const ordinaryManAdd = document.querySelector(`[data-product-id="15108941611394"] [data-action="add"]`);
      if (ordinaryManAdd && event.detail.refillFrequency === 'gift') ordinaryManAdd.click();
    });
  }

  /**
   * Set up listener for gift recipient count changes
   */
  setupGiftRecipientsListener() {
    if (this.giftNotificationWidget) {
      this.giftNotificationWidget.addEventListener('gift-recipients-changed', (e) => {
        this.handleGiftRecipientsChanged(e.detail.recipientCount);
      });
    }
  }

  /**
   * Handle gift recipient count change
   * @param {number} recipientCount - New recipient count
   */
  handleGiftRecipientsChanged(recipientCount) {
    this.giftRecipientCount = recipientCount;
    this.updatePricingSummary();
  }

  /**
   * Bind all event listeners for user interactions
   * - Refill frequency radio buttons
   * - Scent card add/remove buttons
   * - Add to cart button
   */
  bindEvents() {
    //console.log('bindEvents');
    // Color selection event listeners
    const colorInputs = this.element.querySelectorAll('.color-option__input');
    colorInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handleColorSelection(e, e.target.closest('.color-option'));
          if (this.directBuyboxOpen) this.showSelectionInGallery();
        }
      });
    });

    // Refill frequency inputs - exclude gift supply selector
    const refillInputs = this.element.querySelectorAll('.refill-section .refill-option input[type="radio"]');
    refillInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handleRefillSelection(e, e.target.closest('.refill-option'));
        }
      });
    });

    const scentCards = this.element.querySelectorAll('.scent-card');
    scentCards.forEach((card) => {
      const addBtns = card.querySelectorAll('[data-action="add"]');
      const removeBtn = card.querySelector('[data-action="remove"]');

      addBtns.forEach((addBtn) => {
        if (addBtn) {
          addBtn.addEventListener('click', (e) => {
            this.handleScentAdd(e, card);
          });
        }
      });

      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          this.handleScentRemove(e, card);
        });
      }

      // Card click-to-select for single-refill plans (trial, or any plan needing
      // exactly 1 refill pack): no ADD/quantity buttons, the card acts as a radio
      card.addEventListener('click', (e) => {
        if (!this.isSingleScentSelect()) return;
        if (e.target.closest('[data-action]')) return; // ignore button clicks
        if (this.getCardQuantity(card) > 0) return; // already the selected scent
        const addBtn = card.querySelector('.scent-card__add-btn');
        if (addBtn?.classList.contains('scent-card__add-btn--disabled')) return; // sold out / coming soon
        // Deselect the current scent first, then select this one
        this.element.querySelectorAll('.scent-card').forEach((other) => {
          while (other !== card && this.getCardQuantity(other) > 0) {
            this.handleScentRemove(e, other);
          }
        });
        this.handleScentAdd(e, card);
      });
    });

    const addToCartBtn = this.querySelector('[data-add-to-cart-btn]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => {
        this.handleAddToCart({ scroll: true, isTrusted: e.isTrusted });
      });
    }

    const productUpsellButton = this.productUpsell?.querySelector('[data-upsell-toggle]');
    if (productUpsellButton) {
      const useFixedPriceBundle = this.productUpsell.dataset.useFixedPriceBundle === 'true';
      const hasUnavailableItem = (useFixedPriceBundle && this.productUpsell.dataset.bundleAvailable !== 'true') || [...this.productUpsell.querySelectorAll('[data-upsell-item]')].some(
        (item) => item.dataset.available !== 'true'
      );
      productUpsellButton.disabled = hasUnavailableItem;
      productUpsellButton.addEventListener('click', this.handleProductUpsellToggleBinding);
    }
    this.productUpsell?.querySelector('[data-upsell-skip]')?.addEventListener('click', this.handleProductUpsellSkipBinding);

    // Button-link plan toggles (e.g. One Time Purchase, Gift Purchase).
    // There can be more than one, so wire each link to reveal the wrapper whose
    // radio value matches the link's frequency.
    const noSubToggles = this.element.querySelectorAll('[data-no-subscription-toggle]');
    noSubToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const targetFrequency = toggle.dataset.noSubscriptionToggle;
        const wrappers = this.element.querySelectorAll('[data-oneoff-wrapper]');
        let targetWrapper = null;
        wrappers.forEach((wrapper) => {
          const input = wrapper.querySelector('input[type="radio"]');
          if (input && input.value === targetFrequency) targetWrapper = wrapper;
        });
        // Fallback for markup without a frequency value: use the first wrapper.
        if (!targetWrapper) targetWrapper = wrappers[0] || null;
        if (targetWrapper) {
          targetWrapper.style.display = '';
          toggle.style.display = 'none';
          const input = targetWrapper.querySelector('input[type="radio"]');
          if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });
  }

  handleColorSelection(_event, option, force = false) {
    const color = option.dataset.color;
    if (color === this.selectedColor && !force) return;

    this.selectedColor = color;
    this.selectedColorSku = option.dataset.sku;
    this.selectedFeaturedMedia = option.dataset.featuredMedia;

    // Update UI - remove selected class from all color options
    const allColorOptions = this.element.querySelectorAll('.color-option');
    allColorOptions.forEach((opt) => opt.classList.remove('color-option--selected'));
    option.classList.add('color-option--selected');

    const radioInput = option.querySelector('input[type="radio"]');
    if (radioInput) {
      radioInput.checked = true;
    }

    // Update selected color name display
    const colorNameEl = this.element.querySelector('.selected-color-name');
    if (colorNameEl) colorNameEl.textContent = this.selectedColor;

    this.dispatchCustomEvent('colorChanged', {
      selectedColor: this.selectedColor,
      selectedColorSku: this.selectedColorSku,
      selectedFeaturedMedia: this.selectedFeaturedMedia,
    });
  }

  deselectAllColorOptions() {
    const allColorOptions = this.element.querySelectorAll('.color-option');
    allColorOptions.forEach((opt) => opt.classList.remove('color-option--selected'));
    const checkedRadio = this.element.querySelector('.color-option__input:checked');
    if (checkedRadio) checkedRadio.checked = false;
    this.selectedColor = null;
    this.selectedColorSku = null;
    this.selectedFeaturedMedia = null;
  }

  /**
   * Handle refill frequency selection change
   * @param {Event} _event - Click event (unused)
   * @param {HTMLElement} option - Selected refill option element
   *
   * FLOW:
   * 1. Extract frequency from option's data-frequency attribute
   * 2. Skip if already selected (no-op)
   * 3. Update selected frequency and reset scent selections
   * 4. Update UI classes and radio button state
   * 5. Trigger state update (pricing, images, etc.)
   * 6. Dispatch custom event for external listeners
   */
  handleRefillSelection(_event, option) {
    const refillFrequency = option.dataset.frequency;
    const planId = option.dataset.planId || null;
    // Bail only when nothing changed. Two cards can share a frequency, so also
    // compare the plan id to detect switching between same-frequency plans.
    if (refillFrequency === this.selectedRefillFrequency && planId === this.selectedPlanId) return;

    const previousFrequency = this.selectedRefillFrequency;
    this.selectedRefillFrequency = refillFrequency;
    this.selectedPlanId = planId;
    this.resetScentSelections(); // Clear any previous scent choices

    // Update visual selection state
    const allRefillOptions = this.element.querySelectorAll('.refill-option');
    allRefillOptions.forEach((opt) => opt.classList.remove('selected'));
    option.classList.add('selected');

    // Ensure radio button is checked
    const radioInput = option.querySelector('input[type="radio"]');
    if (radioInput) {
      radioInput.checked = true;
    }

    if (this.selectedColor) {
      const firstColorOption = this.element.querySelector('.color-option');
      if (firstColorOption) {
        this.handleColorSelection(null, firstColorOption, true);
      }
    }

    // Hide any button-link wrapper (oneoff / gift) whose card isn't the one being
    // selected, and restore its corresponding link. The matching wrapper stays open.
    const buttonLinkWrappers = this.element.querySelectorAll('[data-oneoff-wrapper]');
    buttonLinkWrappers.forEach((wrapper) => {
      const wrapperInput = wrapper.querySelector('input[type="radio"]');
      const wrapperFrequency = wrapperInput ? wrapperInput.value : 'oneoff';
      if (refillFrequency !== wrapperFrequency) {
        wrapper.style.display = 'none';
        const toggle = this.element.querySelector(`[data-no-subscription-toggle="${wrapperFrequency}"]`);
        if (toggle) toggle.style.display = '';
      }
    });

    // Update all dependent UI elements
    this.updateState();

    // A plan change can swap which gallery media is visible. If the customer
    // has used the gallery arrows first, its scroll position can otherwise
    // point at a slide that is no longer displayed, leaving the carousel blank.
    this.resetGallerySlider();

    // Notify external listeners (e.g., analytics)
    this.dispatchCustomEvent('refillFrequencyChanged', {
      refillFrequency: this.selectedRefillFrequency,
    });
  }

  /**
   * Initialize scent selector to default state
   * Resets selection arrays and updates display
   */
  initializeScentSelector() {
    const planCfg = this.getPlanCardConfig();
    const required = this.getRequiredScentQuantity();
    console.log('[scent-auto-select] initializeScentSelector', {
      refillFrequency: this.selectedRefillFrequency,
      auto_select_all_scents: planCfg.auto_select_all_scents,
      min_scents: planCfg.min_scents,
      requiredQuantity: required,
    });
    if (planCfg.auto_select_all_scents) {
      this.autoSelectAllScents();
    } else {
      this.autoSelectFirstScents(required);
    }
    this.updateScentSelectorDisplay();
    this.updateAddToCartButton();
  }

  /**
   * Auto-select the first N available scents up to the required minimum
   * @param {number} count - Number of scents to auto-select
   */
  autoSelectFirstScents(count) {
    this.selectedScents = [];
    this.selectedScentSkusOrdered = [];
    const allCards = this.element.querySelectorAll('.scent-card');
    const eligibleCards = Array.from(allCards).filter((card) => {
      const addBtn = card.querySelector('.scent-card__add-btn');
      return addBtn && !addBtn.disabled && !addBtn.classList.contains('scent-card__add-btn--disabled');
    });
    console.log('[scent-auto-select] autoSelectFirstScents', { count, totalCards: allCards.length, eligible: eligibleCards.length });
    if (!count || count < 1 || eligibleCards.length === 0) return;

    // Round-robin: loop over eligible cards, adding 1 each pass until count reached
    let added = 0;
    let pass = 0;
    while (added < count) {
      let addedThisPass = 0;
      for (const card of eligibleCards) {
        if (added >= count) break;
        const before = this.getTotalSelectedScentsFromDOM();
        this.handleScentAdd(null, card);
        const after = this.getTotalSelectedScentsFromDOM();
        if (after > before) {
          added++;
          addedThisPass++;
        }
      }
      pass++;
      if (addedThisPass === 0) {
        console.log('[scent-auto-select] stopped — no scents added this pass (cap reached?)', { added, requested: count, pass });
        break;
      }
    }
    console.log('[scent-auto-select] done', { added, requested: count, passes: pass });
  }

  /**
   * @deprecated Use autoSelectFirstScents(1)
   */
  autoSelectFirstScent() {
    this.autoSelectFirstScents(1);
  }

  /**
   * Auto-select available scents for Discovery-style plans, distributing the
   * plan's required quantity as evenly as possible across them (e.g. 16 across
   * 4 scents → 4 each; 14 across 4 → 4,4,3,3). When the required quantity
   * doesn't exceed the scent count, each scent gets 1 (the original behavior).
   * Skips sold-out/coming-soon cards and respects the plan's max_scents cap so
   * a plan configured with auto_select_all_scents + a max (e.g. 2) never
   * pre-selects more than the max number of scents.
   */
  autoSelectAllScents() {
    this.selectedScents = [];
    this.selectedScentSkusOrdered = [];

    const maxScents = this.getPlanCardConfig().max_scents ?? 0;
    const scentCards = this.element.querySelectorAll('.scent-card');

    // Skip sold out / coming soon cards, cap at max_scents (0 = unlimited / all)
    const eligibleCards = Array.from(scentCards).filter((card) => {
      const addBtn = card.querySelector('.scent-card__add-btn');
      return !(addBtn && addBtn.classList.contains('scent-card__add-btn--disabled'));
    });
    const cards = maxScents > 0 ? eligibleCards.slice(0, maxScents) : eligibleCards;
    if (cards.length === 0) return;

    const required = this.getRequiredScentQuantity();
    const base = Math.floor(required / cards.length);
    const remainder = required % cards.length;

    cards.forEach((card, index) => {
      const quantity = Math.max(1, base + (index < remainder ? 1 : 0));

      const productId = card.dataset.productId;
      const variantId = card.dataset.variantId;

      const sellingPlanId = this.getSellingPlanIdForCard(card);

      const title = card.dataset.productTitle || card.querySelector('.scent-card__title')?.textContent?.trim() || '';

      this.selectedScents.push({
        productId,
        variantId,
        quantity,
        sellingPlanId,
        title,
      });

      // Track scent SKU for image matching, once per unit
      if (card.dataset.scentSku) {
        for (let i = 0; i < quantity; i++) {
          this.selectedScentSkusOrdered.push(card.dataset.scentSku);
        }
      }

      // Update DOM to reflect selection
      this.setCardQuantity(card, quantity);
      card.classList.add('scent-card--selected');
    });
  }

  resetScentSelections() {
    this.selectedScents = [];
    this.selectedAddons = [];
    this.addonsTotalPrice = 0;
    this.selectedScentSku = '';
    this.selectedScentSkusOrdered = [];

    const scentCards = this.element.querySelectorAll('.scent-card');
    scentCards.forEach((card) => {
      card.classList.remove('scent-card--selected');

      this.setCardQuantity(card, 0);

      const addButton = card.querySelector('.scent-card__add-btn');
      const quantityControls = card.querySelector('.scent-card__quantity-controls');

      if (addButton) addButton.style.display = 'block';
      if (quantityControls) quantityControls.style.display = 'none';
    });

    if (this.getPlanCardConfig().auto_select_all_scents) {
      this.autoSelectAllScents();
    }

    this.updateScentSelectorDisplay();

    // Auto-select runs AFTER updateScentSelectorDisplay so buttons are re-enabled
    if (!this.getPlanCardConfig().auto_select_all_scents) {
      this.autoSelectFirstScents(this.getRequiredScentQuantity());
    }

    this.updateAddToCartButton();
    this.updatePricingSummary();

  }

  getMappedRefillFrequency() {
    return this.REFILL_FREQUENCY_MAP[this.selectedRefillFrequency];
  }

  /**
   * Get current pricing option based on selected frequency
   * @returns {Object|null} Current pricing option from prices_map
   */
  getCurrentPricingOption() {
    const pricingData = this.prices_map[this.CUSTOMER_TYPE];
    if (!pricingData) return null;

    // Use mapped frequency for all options including gift
    const mappedFrequency = this.getMappedRefillFrequency();
    return pricingData.find((option) => option.frequency === mappedFrequency);
  }

  formatPrice(priceInPence) {
    return Shopify.formatMoney(priceInPence, this.prices_map.money_format).replace('.00', '');
  }

  parseMoneyToPence(value, fallback = 0) {
    if (value == null || value === '') return fallback;
    const parsed = Math.round(parseFloat(String(value).replace(/[^0-9.]/g, '')) * 100);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  usesPlanCardPricing() {
    return (this.configData?.plan_cards_config_blocks_count ?? 0) > 0;
  }

  buildPricesMapFromPlanCards() {
    if (!this.usesPlanCardPricing()) return;

    const cards = this.configData.plan_cards_config;
    const pricingData = this.prices_map[this.CUSTOMER_TYPE];
    if (!pricingData || !cards) return;

    Object.entries(cards).forEach(([freqKey, card]) => {
      const mappedFrequency = this.REFILL_FREQUENCY_MAP[freqKey];
      if (!mappedFrequency) return;

      const option = pricingData.find((o) => o.frequency === mappedFrequency);
      if (!option) return;

      if (card.first_order_price) {
        option.price_cents = this.parseMoneyToPence(card.first_order_price, option.price_cents);
      }

      if (card.original_price) {
        option.price_cents_original = this.parseMoneyToPence(card.original_price, option.price_cents_original);
      } else if (card.first_order_price) {
        option.price_cents_original = option.price_cents;
      }

      if (card.recurring_price && option.refill) {
        const recurringPence = this.parseMoneyToPence(card.recurring_price, option.refill.price_cents * (option.refill.quantity || 1));
        const qty = option.refill.quantity || 1;
        option.refill.price_cents = Math.round(recurringPence / qty);
      }
    });
  }

  getCardQuantity(card) {
    const quantityDisplay = card.querySelector('.scent-card__quantity-display');
    return parseInt(quantityDisplay?.textContent) || 0;
  }

  setCardQuantity(card, quantity) {
    const quantityDisplay = card.querySelector('.scent-card__quantity-display');
    if (quantityDisplay) {
      quantityDisplay.textContent = quantity;
    }
  }

  triggerCardUpdates(card) {
    this.updateSelectedScentsFromDOM();
    this.updateScentCard(card);
    this.updateScentSelectorDisplay();
    this.updateAddToCartButton();
    this.updateState();
  }

  handleScentAdd(_event, card) {
    const currentTotal = this.getTotalSelectedScentsFromDOM();

    // For subscriptions only, limit to required quantity
    // One-time and gift purchases allow unlimited items
    if (this.selectedRefillFrequency !== 'oneoff' && this.selectedRefillFrequency !== 'gift') {
      const requiredQuantity = this.getRequiredScentQuantity();
      if (currentTotal >= requiredQuantity) {
        return;
      }
    }

    const currentQuantity = this.getCardQuantity(card);
    this.setCardQuantity(card, currentQuantity + 1);
    this.selectedScentSkusOrdered.push(card.dataset.scentSku);

    this.triggerCardUpdates(card);

    this.dispatchCustomEvent('scentAdded', {
      productId: card.dataset.productId,
      selectedScents: this.selectedScents,
    });
  }

  handleScentRemove(_event, card) {
    const currentQuantity = this.getCardQuantity(card);

    if (currentQuantity <= 0) {
      return;
    }

    this.setCardQuantity(card, currentQuantity - 1);
    const removeIndex = this.selectedScentSkusOrdered.lastIndexOf(card.dataset.scentSku);
    if (removeIndex !== -1) this.selectedScentSkusOrdered.splice(removeIndex, 1);

    this.triggerCardUpdates(card);

    this.dispatchCustomEvent('scentRemoved', {
      productId: card.dataset.productId,
      selectedScents: this.selectedScents,
    });
  }

  /**
   * Central state update method - triggered whenever selections change
   *
   * ORCHESTRATES:
   * - Stores current state in data attribute (for debugging/external access)
   * - Updates pricing across all options
   * - Updates product images based on frequency
   * - Shows/hides add-ons box for one-time purchases
   * - Updates scent selector counters and button states
   *
   * This is the hub method that keeps all UI in sync with component state.
   */
  updateState() {
    const state = {
      refillFrequency: this.selectedRefillFrequency,
      selectedScents: this.selectedScents,
    };

    // Store state in data attribute for external access/debugging
    this.element.dataset.state = JSON.stringify(state);
    this.mappedRefillFrequency = this.getMappedRefillFrequency();

    // Update all UI elements
    this.updatePricing();
    this.updateDynamicImagery();
    this.updateAddonsVisibility();
    this.updateGiftNotificationVisibility();
    this.updateScentSelectorDisplay();
  }

  updateRefillsPopupText() {
    const refillsPopupText = this.element.querySelector('.refills-popup-text');
    const textHandle = `ongoing_refills_text_once_${this.mappedRefillFrequency}`.toLowerCase().replaceAll(' ', '_');
    if (refillsPopupText) {
      refillsPopupText.innerHTML = this.configData[textHandle];
    }
  }

  updateDynamicImagery() {
    // Updates the small "bars / pack" preview thumbnail rendered inside the
    // picker UI. The PDP hero composition is owned by <scent-canvas>, which
    // listens to picker events directly.
    const barsImage = document.querySelector('.product-includes__item:nth-child(2) img')
      || document.querySelector('.product-includes__item:nth-child(1) img');
    if (!barsImage) return;
    if (!this.defaultBarImgSrcset) this.defaultBarImgSrcset = barsImage.srcset;
    barsImage.srcset = this.defaultBarImgSrcset;
  }

  /**
   * Update all pricing displays throughout the component
   *
   * UPDATES:
   * - Price displays in each refill option card
   * - "Save" badges with discount amounts
   * - Number of bars text
   * - Recurring price text for subscriptions
   * - Pricing summary block
   *
   * PRICING LOGIC:
   * - Reads from this.prices_map based on CUSTOMER_TYPE
   * - Applies frequency-specific pricing
   * - Shows strikethrough for discounted prices
   * - Updates based on selected quantity (barsQty)
   *
   * Called by updateState() whenever selections change.
   */
  updatePricing() {
    // Get the pricing data (now simplified without washing frequency)
    const currentPricingData = this.prices_map[this.CUSTOMER_TYPE];

    if (!currentPricingData) {
      console.error('No pricing data found');
      return;
    }

    // Update refill option prices. Plan-card products key each card by a unique
    // plan id, so two cards may share a frequency (e.g. a 6-week and a 3-month
    // plan both "quarterly") and still be updated independently with their own
    // config. Legacy products (no plan cards) key a single element per frequency.
    if (this.usesPlanCardPricing()) {
      const optionElements = this.element.querySelectorAll('.refill-option[data-plan-id]');
      optionElements.forEach((optionElement) => {
        const planId = optionElement.dataset.planId;
        const freq = optionElement.dataset.frequency;
        const cardConfig = this.configData?.plan_cards_config?.[planId];
        const option = currentPricingData.find((o) => o.frequency === this.REFILL_FREQUENCY_MAP[freq]);
        if (option) this.applyOptionPricing(optionElement, option, cardConfig);
      });
    } else {
      currentPricingData.forEach((option) => {
        let selector;
        let freqKey;
        if (option.frequency === 'Every Month') {
          selector = '[data-frequency="monthly"]';
          freqKey = 'monthly';
        } else if (option.frequency === 'Every 3 Months') {
          selector = '[data-frequency="quarterly"]';
          freqKey = 'quarterly';
        } else if (option.frequency === 'One off Purchase') {
          selector = '[data-frequency="oneoff"]';
          freqKey = 'oneoff';
        } else if (option.frequency === 'Gift Purchase') {
          selector = '[data-frequency="gift"]';
          freqKey = 'gift';
        }
        if (selector) {
          const optionElement = this.element.querySelector(selector);
          if (optionElement) {
            const cardConfig = this.configData?.plan_cards_config?.[freqKey];
            this.applyOptionPricing(optionElement, option, cardConfig);
          }
        }
      });
    }

    // Update pricing summary block
    this.updatePricingSummary();
  }

  /**
   * Apply pricing and display updates to a single plan option element.
   * Uses the plan card's admin-configured display values when present, otherwise
   * falls back to the computed prices_map values.
   * @param {HTMLElement} optionElement - The .refill-option element to update
   * @param {Object} option - The prices_map option for this element's frequency
   * @param {Object} [cardConfig] - The plan card config (admin display values)
   */
  applyOptionPricing(optionElement, option, cardConfig) {
    // Update main price display
    let priceElements = optionElement.querySelectorAll('.price-display');
          let numberOfBarsElements = optionElement.querySelectorAll('[data-refill-number-of-bars]');
          let numberOfBarsElementsV2 = optionElement.querySelectorAll('[data-refill-number-of-bars-v2]');
          let refillDeliveryPriceElements = optionElement.querySelectorAll('[data-refill-delivery-price]');
          let refillTotalPriceElements = optionElement.querySelectorAll('[data-refill-total-price]');
          let savingsAmountElement = optionElement.querySelector('[data-savings-amount]');
          const barsQty = option.refill?.quantity || option.barsQty;
          if (priceElements && priceElements.length > 0) {
            priceElements.forEach((element) => {
              if (cardConfig?.first_order_price) {
                // Use admin-configured display price
                const originalHtml = cardConfig.original_price ? `<span class="original-price">${cardConfig.original_price}</span> ` : '';
                element.innerHTML = `${originalHtml}${cardConfig.first_order_price}`;
              } else if (option.price_cents_original && option.price_cents !== option.price_cents_original) {
                // Show discount pricing with strikethrough
                element.innerHTML = `<span class="original-price">${this.formatPrice(option.price_cents_original)}</span> ${this.formatPrice(option.price_cents)}`;
              } else {
                // Show regular pricing
                element.innerHTML = this.formatPrice(option.price_cents);
              }
            });
          }

          if (numberOfBarsElements) {
            numberOfBarsElements.forEach((element) => {
              element.textContent = `${barsQty * 2} x bar${barsQty * 2 > 1 ? 's' : ''}`;
            });
          }

          numberOfBarsElementsV2.forEach((element) => {
            element.textContent = `${barsQty * 2} Bar${barsQty * 2 > 1 ? 's' : ''}`;
          });

          if (refillDeliveryPriceElements) {
            refillDeliveryPriceElements.forEach((element) => {
              element.textContent = this.formatPrice(option.refill.price_cents);
            });
          }

          if (refillTotalPriceElements) {
            refillTotalPriceElements.forEach((element) => {
              element.textContent = this.formatPrice(option.refill.price_cents * barsQty);
            });
          }

          if (savingsAmountElement) {
            const savingsAmount = option.price_cents_original - option.price_cents;
            const savePercentage = ((option.price_cents_original - option.price_cents) / option.price_cents_original) * 100;
            if (cardConfig?.savings_percentage) {
              // Use admin-configured savings text verbatim (no symbol auto-appended)
              savingsAmountElement.textContent = `Save ${cardConfig.savings_percentage}`;
            } else if (this.dataset.discountType === 'percentage') {
              savingsAmountElement.textContent = `Save ${parseInt(savePercentage)}%`;
            } else {
              savingsAmountElement.textContent = `Save ${this.formatPrice(savingsAmount)}`;
            }
            savingsAmountElement.parentElement.classList.toggle('hidden', parseInt(savePercentage) === 0 && !cardConfig?.savings_percentage);
          }

          // Update recurring text for subscription options
          let recurringElement = optionElement.querySelector('.recurring-text-js');
          if (recurringElement && cardConfig?.recurring_price) {
            recurringElement.textContent = `then ${cardConfig.recurring_price}`;
          } else if (recurringElement && option.refill) {
            recurringElement.textContent = `then ${this.formatPrice(option.refill.price_cents * barsQty)}`;
          }
          const topRefillCount =
            document.querySelector('.product-includes__item:nth-child(2) .product-includes__item-title') ||
            document.querySelector('.product-includes__item:nth-child(1) .product-includes__item-title');
          if (optionElement.querySelector('[name="refill_frequency"]:checked')) {
            if (topRefillCount) topRefillCount.textContent = `${barsQty * 2} x Bob bar${barsQty * 2 > 1 ? 's' : ''}`;

            // Update bob bars quantity in the product includes
            const bobBarsQuantityElement = document.querySelectorAll('.js-bob-bars-quantity');
            bobBarsQuantityElement.forEach((element) => {
              element.textContent = `${barsQty * 2} x Bob bar${barsQty * 2 > 1 ? 's' : ''}`;
              const imageContainer = element.closest('.product-include-item')?.querySelector('img');
              if (imageContainer && imageContainer.hasAttribute('data-4-bars-image') && imageContainer.hasAttribute('data-2-bars-image')) {
                imageContainer.src = imageContainer.dataset[`${barsQty * 2}BarsImage`];
              }
            });
          }
  }

  updatePricingSummary() {
    const selectedOption = this.getCurrentPricingOption();
    if (!selectedOption) return;

    // Prefer the selected plan card's own configured prices. Two cards may share
    // a frequency, so buildPricesMapFromPlanCards() can only bake one card's
    // prices into selectedOption — read the live card config here so the summary
    // is always correct for the specific card selected. Falls back to prices_map.
    const planCard = this.getPlanCardConfig();
    let firstOrderCents = selectedOption.price_cents;
    let firstOrderOriginalCents = selectedOption.price_cents_original;
    if (this.usesPlanCardPricing() && planCard.first_order_price) {
      firstOrderCents = this.parseMoneyToPence(planCard.first_order_price, firstOrderCents);
      firstOrderOriginalCents = planCard.original_price ? this.parseMoneyToPence(planCard.original_price, firstOrderCents) : firstOrderCents;
    }

    const firstOrderPriceEl = this.element.querySelector('.first-order-price');
    const firstOrderPrefix = this.selectedRefillFrequency === 'oneoff' ? '' : 'Your first order: ';
    if (this.selectedRefillFrequency === 'gift' && this.giftRecipientCount > 1) {
      const totalPriceCents = firstOrderCents * this.giftRecipientCount;
      const formattedTotal = this.formatPrice(totalPriceCents);
      const formattedBase = this.formatPrice(firstOrderCents);

      if (firstOrderPriceEl) {
        firstOrderPriceEl.innerHTML = `${firstOrderPrefix}${formattedTotal} <span style="font-weight: 400;">(${this.giftRecipientCount}x ${formattedBase})</span>`;
      }
    } else {
      // Normal pricing display for non-gift or single recipient
      if (firstOrderPriceEl && firstOrderOriginalCents) {
        firstOrderPriceEl.innerHTML = `${firstOrderPrefix}${this.formatPrice(firstOrderCents)} <span class="original-price ${
          firstOrderCents === firstOrderOriginalCents ? 'hidden' : ''
        }">${this.formatPrice(firstOrderOriginalCents)}</span>`;
      } else if (firstOrderPriceEl) {
        firstOrderPriceEl.innerHTML = `${firstOrderPrefix}${this.formatPrice(firstOrderCents)}`;
      }
    }

    const minRefillQty = selectedOption.refill?.quantity || selectedOption.barsQty;
    const refillPriceEl = this.element.querySelector('.refill-price');
    const isOneOffOrGift = selectedOption.frequency === 'One off Purchase' || this.selectedRefillFrequency === 'gift';
    if (refillPriceEl) {
      if (selectedOption.refill && !isOneOffOrGift) {
        // Copy is editable per plan card ("Refill line text") with [period] / [price] placeholders
        const refillLineTemplate = (planCard.refill_line_text || '').trim() || '+ Refills every [period] for [price]';
        let period;
        let price;
        if (planCard.recurring_price) {
          period = planCard.recurring_period || (selectedOption.refill.frequency === 'Every Month' ? 'month' : '3 months');
          price = planCard.recurring_price;
        } else {
          period = selectedOption.refill.frequency === 'Every Month' ? 'month' : '3 months';
          const selectedQty = this.getTotalSelectedScents();
          const refillQty = Math.max(minRefillQty, selectedQty);
          price = this.formatPrice(selectedOption.refill.price_cents * refillQty);
        }
        // [br] starts a new line; each line is set as text (no HTML from settings)
        const refillLines = refillLineTemplate.replace(/\[period\]/gi, period).replace(/\[price\]/gi, price).split(/\[br\]/i);
        refillPriceEl.replaceChildren(
          ...refillLines.flatMap((line, i) => (i === 0 ? [line.trim()] : [document.createElement('br'), line.trim()]))
        );
        refillPriceEl.classList.toggle('refill-price--multiline', refillLines.length > 1);
        refillPriceEl.style.display = '';
      } else {
        refillPriceEl.textContent = '';
        refillPriceEl.style.display = 'none';
      }
    }

    if (this.addonsTotalPrice > 0 && firstOrderPriceEl) {
      const currentFirstOrderPrice = firstOrderCents + this.addonsTotalPrice;
      if (firstOrderOriginalCents && firstOrderOriginalCents !== firstOrderCents) {
        const originalWithAddons = firstOrderOriginalCents + this.addonsTotalPrice;
        firstOrderPriceEl.innerHTML = `${firstOrderPrefix}${this.formatPrice(currentFirstOrderPrice)} <span class="original-price">${this.formatPrice(originalWithAddons)}</span>`;
      } else {
        firstOrderPriceEl.innerHTML = `${firstOrderPrefix}${this.formatPrice(currentFirstOrderPrice)}`;
      }
    }

    this.updateOneOffCardPrice();
  }

  /**
   * Keep the One Time Purchase card's own price in step with the summary:
   * its configured first_order_price plus the extras (extra refills and any
   * add-ons) while that card is selected, and back to the configured price
   * when another plan is chosen. Only for plan cards with a first_order_price;
   * otherwise applyOptionPricing() owns the card's price.
   */
  updateOneOffCardPrice() {
    this.element.querySelectorAll('.refill-option[data-frequency="oneoff"]').forEach((optionElement) => {
      const cardConfig = this.getPlanCardConfig(optionElement.dataset.planId || 'oneoff');
      if (!cardConfig?.first_order_price) return;

      const isSelected = this.selectedRefillFrequency === 'oneoff' && !!optionElement.querySelector('[name="refill_frequency"]:checked');
      const extrasCents = isSelected ? this.addonsTotalPrice : 0;
      const originalText =
        cardConfig.original_price && extrasCents > 0
          ? this.formatPrice(this.parseMoneyToPence(cardConfig.original_price) + extrasCents)
          : cardConfig.original_price;
      const originalHtml = originalText ? `<span class="original-price">${originalText}</span> ` : '';
      const priceText =
        extrasCents > 0
          ? this.formatPrice(this.parseMoneyToPence(cardConfig.first_order_price) + extrasCents)
          : cardConfig.first_order_price;

      optionElement.querySelectorAll('.price-display').forEach((element) => {
        element.innerHTML = `${originalHtml}${priceText}`;
      });
    });
  }

  dispatchCustomEvent(eventName, detail) {
    //console.log('dispatchCustomEvent', eventName, detail);
    const event = new CustomEvent(`subscription-picker:${eventName}`, {
      detail: detail,
      bubbles: true,
    });
    this.element.dispatchEvent(event);
    //window.dispatchEvent(event);
  }

  /**
   * Get product price for addon calculation
   * @param {string} _productId - Product ID (unused but kept for signature)
   * @param {string} _variantId - Variant ID (unused but kept for signature)
   * @returns {number} Price in pence
   */
  getProductPrice(_productId, _variantId) {
    const currentOption = this.getCurrentPricingOption();
    return currentOption?.per_refill_pack_cost_cents || this.SCENT_ADDON_PRICE_PENCE;
  }

  getPlanCardConfig(key) {
    const cfgs = this.configData?.plan_cards_config;
    if (!cfgs) return {};
    // Prefer an explicit key, then the selected plan id (plan-card products),
    // then the frequency (legacy products keyed by frequency).
    const lookup = key ?? this.selectedPlanId ?? this.selectedRefillFrequency;
    return cfgs[lookup] ?? cfgs[this.selectedRefillFrequency] ?? {};
  }

  getSellingPlanIdForCard(card) {
    const cfg = this.getPlanCardConfig();
    if (!cfg.billing_interval) return '';
    try {
      const plans = JSON.parse(card.dataset.sellingPlans || '[]');
      // Auto-detect selling plan index based on frequency
      // monthly → index 0, quarterly → index 1
      // NOTE: two cards sharing a frequency (e.g. 6-week vs 3-month, both
      // "quarterly") currently resolve to the same selling plan. Making these
      // distinct requires per-card Recharge selling-plan config that isn't set
      // today, so we keep the frequency-based mapping.
      let index = 0;
      if (this.selectedRefillFrequency === 'quarterly') {
        index = 1;
      }
      const plan = plans[index];
      return plan ? String(plan.id) : '';
    } catch {
      return '';
    }
  }

  /**
   * Get the required quantity of scents based on refill frequency
   * @returns {number} Required scent quantity
   */
  getRequiredScentQuantity() {
    const cfg = this.getPlanCardConfig();
    if (this.configData?.plan_cards_config_blocks_count > 0 && cfg.min_scents != null) return cfg.min_scents;
    const selectedOption = this.getCurrentPricingOption();
    return selectedOption?.refill?.quantity || selectedOption?.barsQty || 1;
  }

  /**
   * Subscription plans that take exactly 1 refill pack use a direct
   * single-select picker (click a card to choose it) instead of ADD/+/- controls.
   * One-time and gift purchases allow multiple packs, so they never qualify.
   * @returns {boolean}
   */
  isSingleScentSelect() {
    if (this.selectedRefillFrequency === 'oneoff' || this.selectedRefillFrequency === 'gift') return false;
    return this.getRequiredScentQuantity() === 1;
  }

  /**
   * Get total number of selected scents from DOM
   * @returns {number} Total selected quantity
   */
  getTotalSelectedScentsFromDOM() {
    const scentCards = this.element.querySelectorAll('.scent-card');
    let total = 0;

    scentCards.forEach((card) => {
      const quantityDisplay = card.querySelector('.scent-card__quantity-display');
      const quantity = parseInt(quantityDisplay?.textContent) || 0;
      total += quantity;
    });

    return total;
  }

  /**
   * Get total number of selected scents
   * @returns {number} Total selected quantity
   */
  getTotalSelectedScents() {
    return this.selectedScents.reduce((total, scent) => total + scent.quantity, 0);
  }

  /**
   * Update selected scents array from current DOM state
   * Reads quantities directly from DOM and rebuilds the selectedScents array
   *
   * IMPORTANT: This is a key method called whenever scents are added/removed.
   * It synchronizes the component's internal state with what's displayed in the UI.
   *
   * LOGIC FLOW:
   * 1. Reset both selectedScents and selectedAddons arrays
   * 2. Iterate through all scent cards, collecting items with quantity > 0
   * 3. For each item, determine the correct selling plan based on frequency
   * 4. For one-time/gift purchases:
   *    - Split items into "included" (up to requiredQuantity) and "addons" (extras)
   *    - Included items go to selectedScents (part of base price)
   *    - Extra items go to selectedAddons (charged at £8 each)
   * 5. For subscriptions:
   *    - All items go to selectedScents (no addon concept)
   *    - Items are grouped by variant ID to consolidate duplicates
   * 6. Recalculate addon pricing and update the pricing summary
   *
   * DATA FLOW:
   * DOM (quantity displays) → allItems array → selectedScents/selectedAddons arrays
   * → calculateAddonsTotal() → updatePricingSummary() → UI updates
   */
  updateSelectedScentsFromDOM() {
    // Reset state before rebuilding from DOM
    this.selectedScents = [];
    this.selectedAddons = [];

    const scentCards = this.element.querySelectorAll('.scent-card');
    const allItems = []; // Temporary array to collect all selected items
    const requiredQuantity = this.getRequiredScentQuantity();

    /**
     * FIRST PASS: Collect all selected items from DOM
     * Creates individual item entries for each unit selected.
     * Example: If "Ordinary Man" has quantity 2, creates 2 separate items.
     * This allows proper splitting between base items and addons later.
     */
    scentCards.forEach((card) => {
      const quantityDisplay = card.querySelector('.scent-card__quantity-display');
      const quantity = parseInt(quantityDisplay?.textContent) || 0;

      if (quantity > 0) {
        const productId = card.dataset.productId;
        const variantId = card.dataset.variantId;
        const sellingPlansData = card.dataset.sellingPlans;
        const title = card.dataset.productTitle || card.querySelector('.scent-card__title')?.textContent?.trim() || '';

        const sellingPlanId = this.getSellingPlanIdForCard(card);

        /**
         * Create individual items for each unit of quantity.
         * This allows us to properly distribute items between
         * selectedScents (included in price) and selectedAddons (extra charge).
         */
        for (let i = 0; i < quantity; i++) {
          allItems.push({
            productId,
            variantId,
            sellingPlanId,
            title,
          });
        }
      }
    });

    // For one-time and gift purchases, split between selectedScents and selectedAddons
    if (this.selectedRefillFrequency === 'oneoff' || this.selectedRefillFrequency === 'gift') {
      // First requiredQuantity items go to selectedScents (included in base price)
      if (allItems.length > 0) {
        const groupedScents = {};
        const addonItems = {};

        // Split items: first requiredQuantity go to selectedScents
        for (let i = 0; i < allItems.length; i++) {
          const item = allItems[i];
          const key = item.variantId;

          if (i < requiredQuantity) {
            // Add to selectedScents
            if (groupedScents[key]) {
              groupedScents[key].quantity += 1;
            } else {
              groupedScents[key] = {
                productId: item.productId,
                variantId: item.variantId,
                quantity: 1,
                sellingPlanId: item.sellingPlanId,
                title: item.title,
              };
            }
          } else {
            // Add to selectedAddons (items beyond requiredQuantity)
            if (addonItems[key]) {
              addonItems[key].quantity += 1;
            } else {
              addonItems[key] = {
                variantId: item.variantId,
                productId: item.productId,
                quantity: 1,
                price: this.getProductPrice(item.productId, item.variantId),
                isSameVariant: false,
              };
            }
          }
        }

        //console.log('addonItems', addonItems);
        // Convert to arrays
        this.selectedScents = Object.values(groupedScents);
        this.selectedAddons = Object.values(addonItems);
      }
    } else {
      // For subscriptions, group by variant
      const groupedItems = {};
      allItems.forEach((item) => {
        const key = item.variantId;
        if (groupedItems[key]) {
          groupedItems[key].quantity += 1;
        } else {
          groupedItems[key] = {
            productId: item.productId,
            variantId: item.variantId,
            quantity: 1,
            sellingPlanId: item.sellingPlanId,
            title: item.title,
          };
        }
      });

      this.selectedScents = Object.values(groupedItems);
    }

    // Recalculate addon pricing
    this.calculateAddonsTotal();
    this.updatePricingSummary();
  }

  /**
   * Update scent card display based on current selection
   * @param {HTMLElement} card - The scent card to update
   */
  updateScentCard(card) {
    const quantity = this.getCardQuantity(card);

    const addBtns = card.querySelectorAll('[data-action="add"]');
    const removeBtn = card.querySelector('[data-action="remove"]');
    const addButton = card.querySelector('.scent-card__add-btn');
    const quantityControls = card.querySelector('.scent-card__quantity-controls');

    // Update remove button icon based on quantity
    if (removeBtn) {
      removeBtn.disabled = quantity === 0;
      //removeBtn.disabled = addButton.classList.contains('scent-card__add-btn--disabled');

      const trashIcon = removeBtn.querySelector('.icon-trash');
      const minusIcon = removeBtn.querySelector('.icon-minus');

      if (trashIcon && minusIcon) {
        if (quantity === 1) {
          // Show trash icon when quantity is 1 (will remove item completely)
          trashIcon.style.display = 'block';
          minusIcon.style.display = 'none';
        } else {
          // Show minus icon when quantity > 1 (will decrease quantity)
          trashIcon.style.display = 'none';
          minusIcon.style.display = 'block';
        }
      }
    }

    const requiredQuantity = this.getRequiredScentQuantity();
    const currentTotal = this.getTotalSelectedScentsFromDOM();
    if (currentTotal > 0) {
      const topRefillCount =
        document.querySelector('.product-includes__item:nth-child(2) .product-includes__item-title') ||
        document.querySelector('.product-includes__item:nth-child(1) .product-includes__item-title');
      if (topRefillCount) topRefillCount.textContent = `${currentTotal * 2} x Bob bar${currentTotal * 2 > 1 ? 's' : ''}`;
      // Update bob bars quantity in the product includes
      const bobBarsQuantityElement = document.querySelectorAll('.js-bob-bars-quantity');
      bobBarsQuantityElement.forEach((element) => {
        element.textContent = `${currentTotal * 2} x Bob bar${currentTotal * 2 > 1 ? 's' : ''}`;
      });
    }

    const planMax = this.getPlanCardConfig().max_scents ?? 0;
    const canAddMore = planMax === 0 ? true : currentTotal < planMax;

    // Update all add buttons
    addBtns.forEach((addBtn) => {
      if (addBtn) {
        addBtn.disabled = !canAddMore;
        addBtn.disabled = addBtn.classList.contains('scent-card__add-btn--disabled');
      }
    });

    // Toggle between ADD button and quantity controls
    if (quantity > 0) {
      card.classList.add('scent-card--selected');
      if (addButton) addButton.style.display = 'none';
      if (quantityControls) quantityControls.style.display = 'flex';
      this.selectedScentSku = card.dataset.scentSku;
    } else {
      card.classList.remove('scent-card--selected');
      if (addButton) addButton.style.display = 'block';
      if (quantityControls) quantityControls.style.display = 'none';
    }

    if (this._initialized) {
      this.dispatchCustomEvent('scentCardUpdated', { scentSku: this.selectedScentSku, selectedScentSkusOrdered: this.selectedScentSkusOrdered });
    }

    // Disable ADD button if we can't add more
    if (addButton) {
      addButton.disabled = !canAddMore;
    }
    //debugger;
  }

  /**
   * Update all scent selector displays
   */
  updateScentSelectorDisplay() {
    const scentWidget = this.element.querySelector('.scent-selector-widget');
    if (!scentWidget) return;

    const requiredQuantity = this.getRequiredScentQuantity();
    const currentTotal = this.getTotalSelectedScentsFromDOM();

    // Toggle between discovery title and standard title
    const discoveryTitle = scentWidget.querySelector('.scent-title-discovery');
    const oneoffTitle = scentWidget.querySelector('.scent-title-oneoff');
    const subtitleElement = scentWidget.querySelector('.scent-selector-subtitle');
    const selectedCountSpan = scentWidget.querySelector('.selected-count');
    const requiredCountSpan = scentWidget.querySelector('.required-count');

    // Single-refill plans: cards become direct options (see isSingleScentSelect)
    const singleScentSelect = this.isSingleScentSelect();
    this.element.toggleAttribute('data-single-scent', singleScentSelect);

    if (this.getPlanCardConfig().auto_select_all_scents && !singleScentSelect) {
      //this.element.setAttribute('data-auto-select-scents', ''); Pre-select BUT, do not lock user slection
      if (discoveryTitle) discoveryTitle.style.display = '';
      if (oneoffTitle) oneoffTitle.style.display = 'none';
    } else {
      this.element.removeAttribute('data-auto-select-scents');
      // All other modes: show standard title with counter
      if (discoveryTitle) discoveryTitle.style.display = 'none';
      if (oneoffTitle) oneoffTitle.style.display = '';
    }

    // Replace the "[x]" placeholder in the discovery title with the plan's fixed
    // refill-pack limit (getRequiredScentQuantity → the plan card's min_scents,
    // which equals max_scents for every auto-select-all plan). We use the plan
    // limit rather than the live selection count so the title stays stable if the
    // customer adjusts quantities. We cache the authored title on first run so the
    // placeholder survives being overwritten and can be re-applied. Titles without
    // "[x]" are left exactly as authored.
    if (discoveryTitle) {
      const bundleTitleEl = discoveryTitle.querySelector('li');
      if (bundleTitleEl) {
        if (this.bundleTitleTemplate == null) {
          this.bundleTitleTemplate = bundleTitleEl.textContent;
        }
        if (/\[x\]/i.test(this.bundleTitleTemplate)) {
          bundleTitleEl.textContent = this.bundleTitleTemplate.replace(/\[x\]/gi, requiredQuantity);
        }
      }
    }

    if (subtitleElement) {
      const planCfg = this.getPlanCardConfig();
      const defaultSubtitle = (this.configData?.bundle_products_default_subtitle ?? '').trim();

      if (planCfg.hide_bundle_subtitle) {
        subtitleElement.textContent = '';
        subtitleElement.hidden = true;
        subtitleElement.style.display = 'none';
      } else {
        const override = (planCfg.bundle_subtitle_override ?? '').trim();
        const subtitleText = override || defaultSubtitle;

        if (subtitleText) {
          subtitleElement.textContent = subtitleText;
          subtitleElement.hidden = false;
          subtitleElement.style.display = 'block';
        } else {
          subtitleElement.textContent = '';
          subtitleElement.hidden = true;
          subtitleElement.style.display = 'none';
        }
      }
    }

    // Update counter spans if they exist (for subscription mode)
    if (selectedCountSpan) {
      selectedCountSpan.textContent = currentTotal;
    }
    if (requiredCountSpan) {
      requiredCountSpan.textContent = requiredQuantity;
    }
    // One-time customers can choose as many scents as they like, so the
    // "(selected/required)" count only applies to subscriptions.
    const titleCount = scentWidget.querySelector('.scent-title-count');
    if (titleCount) {
      titleCount.hidden = this.selectedRefillFrequency === 'oneoff';
    }

    if (currentTotal > requiredQuantity) {
      this.element.setAttribute('data-scents-over-min', 'true');
    } else {
      this.element.removeAttribute('data-scents-over-min');
    }

    // Update all cards
    const cards = scentWidget.querySelectorAll('.scent-card');
    cards.forEach((card) => this.updateScentCard(card));
  }

  /**
   * Update add to cart button state based on scent selection
   */
  updateAddToCartButton() {
    const addToCartBtn = this.element.querySelector('[data-add-to-cart-btn]');
    const btnText = this.element.querySelector('[data-btn-text]');
    const currentTotal = this.getTotalSelectedScentsFromDOM();
    if (this.element.dataset.closed === 'true') return;
    if (!addToCartBtn || !btnText) return;

    // For subscriptions, follow exact quantity requirements
    const requiredQuantity = this.getRequiredScentQuantity();
    const remaining = requiredQuantity - currentTotal;

    if (remaining > 0) {
      addToCartBtn.disabled = true;
      btnText.textContent = `Add ${remaining} refill${remaining > 1 ? 's' : ''} to checkout`;
    } else {
      addToCartBtn.disabled = false;
      btnText.textContent = 'Checkout';
    }
  }

  // Public methods for external access
  getState() {
    return {
      refillFrequency: this.selectedRefillFrequency,
      selectedScents: this.selectedScents,
    };
  }

  setRefillFrequency(refillFrequency) {
    const option = this.element.querySelector(`[data-frequency="${refillFrequency}"]`);
    if (option && option.classList.contains('refill-option')) {
      const radioInput = option.querySelector('input[type="radio"]');
      if (radioInput) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  hasAddonsContent() {
    return Boolean(this.addonsBox?.querySelector('select[name="addon_product"]'));
  }

  updateAddonsVisibility() {
    // Show add-ons box for plans that opt in via the plan card's show_addons
    // setting, when addon products are configured on the product metafield.
    if (this.addonsBox) {
      if (this.getPlanCardConfig().show_addons && this.hasAddonsContent()) {
        this.addonsBox.style.display = '';
        this.setupAddonsEventListeners();
      } else {
        this.addonsBox.style.display = 'none';
        this.resetAddons();
      }
    }
  }

  /**
   * Update gift notification widget visibility
   * Show only when 'gift' refill frequency is selected
   */
  updateGiftNotificationVisibility() {
    // Bespoke gift UI is opt-in per gift plan card. When disabled the gift card
    // behaves like a standard card: no recipient widget, colour picker stays visible.
    const planCard = this.getPlanCardConfig();
    const useBespokeGiftUi = this.selectedRefillFrequency === 'gift' && planCard.show_gift_card_recipient !== false;

    if (this.giftNotificationWidget) {
      this.giftNotificationWidget.style.display = useBespokeGiftUi ? '' : 'none';
    }
    this.element.setAttribute('data-gift-selected', useBespokeGiftUi ? 'true' : 'false');
  }

  setupAddonsEventListeners() {
    // Add event listeners to addon select dropdowns
    const addonSelects = this.addonsBox.querySelectorAll('select[name="addon_product"]');
    addonSelects.forEach((select) => {
      select.removeEventListener('change', this.handleAddonChangeBinding);
      select.addEventListener('change', this.handleAddonChangeBinding);
    });
  }

  handleAddonChange(_event) {
    this.calculateAddonsTotal();
    this.updatePricingSummary();
  }

  handleProductUpsellToggle() {
    if (!this.productUpsell) return;

    this.productUpsellSelected = !this.productUpsellSelected;
    this.productUpsell.classList.toggle('is-selected', this.productUpsellSelected);

    const button = this.productUpsell.querySelector('[data-upsell-toggle]');
    if (button) {
      button.setAttribute('aria-pressed', String(this.productUpsellSelected));
      button.textContent = this.productUpsellSelected ? button.dataset.selectedLabel : button.dataset.defaultLabel;
    }

    this.calculateAddonsTotal();
    this.updatePricingSummary();
  }

  handleProductUpsellSkip(event) {
    this.handleAddToCart({ scroll: true, isTrusted: event?.isTrusted, bypassUpsell: true });
  }

  showProductUpsellStep() {
    if (!this.productUpsell) return false;
    // window._uxa.push(['setCustomVariable', 1, window.product_handle, 'upsell', 3]);
    // window._uxa.push(['trackPageview']);
    this.element.dataset.currentStep = '3';
    this.element.dataset.upsellStep = 'true';
    const btnText = this.element.querySelector('[data-add-to-cart-btn] .btn-text');
    if (btnText) btnText.textContent = 'Checkout';
    this.updateAddToCartButton();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    return true;
  }

  calculateAddonsTotal() {
    let total = 0;

    // Calculate traditional add-ons (from add-ons box)
    if (this.addonsBox) {
      const addonSelects = this.addonsBox.querySelectorAll('select[name="addon_product"]');
      addonSelects.forEach((select) => {
        const quantity = parseInt(select.value) || 0;
        const price = parseInt(select.dataset.variantPrice) || 0;
        total += quantity * price;
      });
    }

    // Calculate scent add-ons (for one-time purchases)
    if (this.selectedAddons && this.selectedAddons.length > 0) {
      this.selectedAddons.forEach((addon) => {
        total += addon.quantity * addon.price;
      });
    }

    if (this.productUpsellSelected && this.productUpsell) {
      total += parseInt(this.productUpsell.dataset.offerPrice, 10) || 0;
    }

    this.addonsTotalPrice = total;
    return total;
  }

  resetAddons() {
    // Reset all addon selections to 0
    if (this.addonsBox) {
      const addonSelects = this.addonsBox.querySelectorAll('select[name="addon_product"]');
      addonSelects.forEach((select) => {
        select.value = '0';
      });
      // Recalculate rather than zero the total: it also holds the extra scent
      // refills of a one-time/gift purchase (selectedAddons), which this hidden
      // add-ons box doesn't own. Zeroing it here (called on every updateState
      // when the plan card has show_addons off) froze the one-time price.
      this.calculateAddonsTotal();
      // Update pricing to remove add-ons from the summary
      this.updatePricingSummary();
    }
  }

  getSelectedAddons() {
    const selectedAddons = [];

    // Get traditional add-ons from the add-ons box
    if (this.addonsBox && this.selectedRefillFrequency === 'oneoff') {
      const addonSelects = this.addonsBox.querySelectorAll('select[name="addon_product"]');
      addonSelects.forEach((select) => {
        const quantity = parseInt(select.value) || 0;
        if (quantity > 0) {
          const isSameVariant = select.dataset.sameVariant === 'true';
          const variantsIds = select.dataset.variantsIds;

          // If we have the new data-variants-ids attribute, parse it and create items for each variant
          if (variantsIds) {
            try {
              const variantIdsArray = JSON.parse(variantsIds);
              variantIdsArray.forEach((variantId) => {
                selectedAddons.push({
                  id: variantId,
                  quantity: quantity,
                  isSameVariant: isSameVariant,
                });
              });
            } catch (e) {
              console.error('Error parsing data-variants-ids:', e);
              // Fallback to single variant if parsing fails
              const variantId = select.dataset.variantId;
              selectedAddons.push({
                id: variantId,
                quantity: quantity,
                isSameVariant: isSameVariant,
              });
            }
          } else {
            // Fallback to old single variant approach
            const variantId = select.dataset.variantId;
            selectedAddons.push({
              id: variantId,
              quantity: quantity,
              isSameVariant: isSameVariant,
            });
          }
        }
      });
    }

    // Add scent add-ons (for one-time purchases with multiple scents)
    if (this.selectedAddons && this.selectedAddons.length > 0) {
      this.selectedAddons.forEach((addon) => {
        selectedAddons.push({
          id: addon.variantId,
          quantity: addon.quantity,
          isSameVariant: addon.isSameVariant,
        });
      });
    }

    if (this.productUpsellSelected && this.productUpsell) {
      const useFixedPriceBundle = this.productUpsell.dataset.useFixedPriceBundle === 'true';
      if (!useFixedPriceBundle) {
        this.productUpsell.querySelectorAll('[data-upsell-item]').forEach((item) => {
          const variantId = parseInt(item.dataset.variantId, 10);
          const quantity = parseInt(item.dataset.quantity, 10) || 1;
          if (variantId) {
            selectedAddons.push({
              id: variantId,
              quantity,
              properties: {
                _upsell_offer: this.productUpsell.dataset.offerKey,
              },
            });
          }
        });
        return selectedAddons;
      }

      const bundleVariantId = parseInt(this.productUpsell.dataset.bundleVariantId, 10);
      const bundleProductId = parseInt(this.productUpsell.dataset.bundleProductId, 10);
      const bundleSelections = [];

      this.productUpsell.querySelectorAll('[data-bundle-selection]').forEach((item) => {
        const variantId = parseInt(item.dataset.variantId, 10);
        const quantity = parseInt(item.dataset.quantity, 10) || 1;
        if (variantId) {
          bundleSelections.push({
            collectionId: parseInt(item.dataset.collectionId, 10),
            externalProductId: parseInt(item.dataset.productId, 10),
            externalVariantId: variantId,
            quantity,
          });
        }
      });

      if (bundleVariantId && bundleProductId && bundleSelections.every((selection) => selection.collectionId && selection.externalProductId)) {
        selectedAddons.push({
          bundle: {
            externalProductId: bundleProductId,
            externalVariantId: bundleVariantId,
            selections: bundleSelections,
          },
          properties: {
            _upsell_offer: this.productUpsell.dataset.offerKey,
            _bundle_contents: [...this.productUpsell.querySelectorAll('[data-bundle-selection]')]
              .map((item) => `${parseInt(item.dataset.quantity, 10) || 1} x ${item.dataset.title || 'Bundle product'}`)
              .join('\n'),
          },
        });
      }
    }

    return selectedAddons;
  }

  /**
   * Handle Add to Cart button click
   *
   * TWO-STAGE FLOW:
   * 1. If picker is closed (data-closed="true"):
   *    - Expand picker to show scent selector
   *    - Change button text to "Checkout"
   *    - Return early (don't add to cart yet)
   *
   * 2. If picker is open (data-closed="false"):
   *    - Validate scent selection quantity
   *    - Get current pricing option
   *    - Build cart data object
   *    - Dispatch 'addToCartRequested' event
   *    - mps--add-to-cart.js listens for this event and handles Recharge integration
   *
   * VALIDATION:
   * - Subscriptions: Must select exact required quantity
   * - One-time: Must select at least 1 scent (unlimited allowed)
   */
  handleAddToCart({ scroll = false, isTrusted = false, bypassUpsell = false } = {}) {
    const addToCartBtn = this.element.querySelector('[data-add-to-cart-btn]');

    // STAGE 1: Expand picker if collapsed
    if (this.element.dataset.closed === 'true') {
      if (typeof theme.logGalleryInteraction === 'function') {
        theme.logGalleryInteraction('continue', {
          currentStep: this.element.dataset.currentStep || null,
          scrollLeft: document.querySelector('slider-element[id^="SliderGallery-"]')?.scrollLeft ?? null,
        });
      }
      console.log('[months-gallery] interaction', {
        action: 'continue',
        currentStep: this.element.dataset.currentStep || null,
        scrollLeft: document.querySelector('slider-element[id^="SliderGallery-"]')?.scrollLeft ?? null,
      });
      const combineMobile = this.element.dataset.combineStepsMobile === 'true';
      const isMobile = window.innerWidth < 768;

      this.element.setAttribute('data-closed', 'false');
      // window._uxa.push(['setCustomVariable', 1, window.product_handle, 'continue', 3]);
      // window._uxa.push(['trackPageview']);
      this.element.dataset.currentStep = '2';
      addToCartBtn.querySelector('.btn-text').innerText = 'Checkout';

      // Track widget open in GA4 (only for human clicks)
      if (isTrusted && typeof window.gtag === 'function') {
        window.gtag('event', 'subscription_widget_opened', {
          refill_frequency: this.selectedRefillFrequency,
        });
      }

      if (this.element.querySelector('.color-option--selected')) this.handleColorSelection(null, this.element.querySelector('.color-option--selected'), true);
      this.updateAddToCartButton();
      this.resetGallerySlider();
      this.dispatchCustomEvent('scentCardUpdated', { scentSku: this.selectedScentSku, selectedScentSkusOrdered: this.selectedScentSkusOrdered });
      // Scroll to top of picker when changing steps
      //if (scroll && window.innerWidth < 1024 && document.location.pathname.includes('/products/')) this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // On mobile with combine_steps_mobile enabled, skip the pause at step 1
      // and fall through directly to cart validation (Stage 2)
      if (!(combineMobile && isMobile)) return;
    }

    // STAGE 2: Validate and prepare cart data
    // Validate scent selection before proceeding
    const currentTotal = this.getTotalSelectedScentsFromDOM();

    const planCfg = this.getPlanCardConfig();
    if (!planCfg.auto_select_all_scents) {
      const min = planCfg.min_scents ?? this.getRequiredScentQuantity();
      const max = planCfg.max_scents ?? 0;
      const exactRequired = max > 0 && min === max;
      const tooFew = currentTotal < min;
      const tooMany = max > 0 && currentTotal > max;
      if ((exactRequired && currentTotal !== min) || (!exactRequired && (tooFew || tooMany))) {
        console.warn('Cannot add to cart: Incorrect scent selection quantity');
        return;
      }
    }

    // Get current selected options
    const selectedRefillFrequency = this.selectedRefillFrequency;

    // Validate gift notification data for gift purchases
    if (selectedRefillFrequency === 'gift' && this.giftNotificationWidget) {
      const validation = this.giftNotificationWidget.validateGiftNotification();
      if (!validation.isValid) {
        console.warn('Gift notification validation failed:', validation.errors);
        // Show errors in the form
        this.giftNotificationWidget.showErrors(validation.errors);
        return;
      }
    }
    if(window.skiptheUpsell){
      bypassUpsell = window.skiptheUpsell;
    }
    if (!bypassUpsell && this.productUpsell && this.element.dataset.upsellStep !== 'true') {
      this.showProductUpsellStep();
      return;
    }

    // Find the current product data based on selections
    const selectedOption = this.getCurrentPricingOption();

    if (!selectedOption) {
      console.error('No product option found for current selection');
      return;
    }

    const addToCartData = {
      refillFrequency: selectedRefillFrequency,
      planId: this.selectedPlanId,
      selectedScents: this.selectedScents,
      selectedColor: this.selectedColor,
      productData: selectedOption,
      selectedAddons: this.getSelectedAddons(),
      pricing: {
        firstOrderPrice: selectedOption.price_cents,
        originalPrice: selectedOption.price_cents_original || selectedOption.price_cents,
        refillPrice: selectedOption.refill ? selectedOption.refill.price_cents : null,
        addonsTotal: this.addonsTotalPrice,
      },
    };
    // window._uxa.push(['setCustomVariable', 1, window.product_handle, 'final-step', 3]);
    // window._uxa.push(['trackPageview']);

    // Include gift notification data for gift purchases
    if (selectedRefillFrequency === 'gift' && this.giftNotificationWidget) {
      const giftNotificationData = this.giftNotificationWidget.getGiftNotificationData();
      if (giftNotificationData) {
        addToCartData.giftNotification = giftNotificationData;
        addToCartData.giftNotification.klaviyoList = this.giftNotificationWidget.getKlaviyoListName();
      }
    }

    // Dispatch custom event with the selected data
    this.dispatchCustomEvent('addToCartRequested', addToCartData);
  }

  updateTrialRenewalDate() {
    const renewalDateEl = this.element.querySelector('[data-trial-renewal-date]');
    if (!renewalDateEl) return;
    const renewalDate = new Date();
    renewalDate.setDate(renewalDate.getDate() + 14);
    const day = renewalDate.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
    const month = renewalDate.toLocaleDateString('en-GB', { month: 'long' });
    const year = renewalDate.getFullYear();
    renewalDateEl.textContent = `${day}${suffix} ${month} ${year}`;
  }
}

customElements.define('subscription-picker', MonthsSubscriptionPicker);

document.addEventListener('DOMContentLoaded', function () {
  var ctaButton = document.querySelector('.bodywash-cta');

  if (ctaButton) {
    ctaButton.addEventListener('click', function () {
      // Get selected refill frequency value
      var refillFrequencyEl = document.querySelector('[name="refill_frequency"]:checked');
      var refillFrequency = refillFrequencyEl ? refillFrequencyEl.value : null;

      // Extract the product handle from the current URL
      // e.g. /products/xl-bars -> xl-bars
      var pathParts = window.location.pathname.split('/').filter(Boolean);
      var handle = pathParts[pathParts.length - 1];

      // Build redirect URL
      var redirectUrl = '/pages/' + handle;

      // Optional: pass refill frequency along as a query param
      if (refillFrequency != "quarterly") {
        redirectUrl += '?refill_frequency=' + encodeURIComponent(refillFrequency);
      }
      // window._uxa.push(['setCustomVariable', 1, window.product_handle, 'continue', 3]);
      // window._uxa.push(['trackPageview']);
      setTimeout(function() { 
        window.location.href = redirectUrl;
      }, 500);
    });
  }
});